import { useCallback, useEffect, useRef, useState } from "react";
import { buildWsURL } from "../lib/api";

/**
 * useRunStream
 *
 * Hook that owns a WebSocket connection to /api/run/stream.
 *
 * It exposes:
 *   - run({ language, code, stdin?, timeout? }) -> opens a fresh WS for this run
 *   - stop() -> sends {type:"stop"} on the live WS
 *   - isRunning, status (idle|running|success|error|stopped|timed_out)
 *   - lastResult: aggregated {stdout, stderr, compile_error, exit_code,
 *     exec_time_ms, timed_out, stopped} populated when the run finishes
 *
 * Streaming events are forwarded to the optional callbacks supplied at hook
 * setup so the consumer (App.js) can pipe them straight into xterm.
 *
 * Important: backend `compile_error` frame is a metadata marker only, the
 * actual compile message arrives as `stderr` chunks during the `compile`
 * stage. We buffer them and surface the joined text as compile_error.
 */
export default function useRunStream({
  onStart,
  onStage,
  onStdout,
  onStderr,
  onCompileError,
  onExit,
  onError,
  onClose,
} = {}) {
  const wsRef = useRef(null);
  const stageRef = useRef(null);
  const stdoutBufRef = useRef("");
  const stderrBufRef = useRef("");
  const compileStderrRef = useRef("");
  const startedAtRef = useRef(0);

  const [status, setStatus] = useState("idle");
  const [isRunning, setIsRunning] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [activeRunId, setActiveRunId] = useState(null);

  // Latest callbacks via ref so consumers don't trigger reconnects on changes
  const cbRef = useRef({ onStart, onStage, onStdout, onStderr, onCompileError, onExit, onError, onClose });
  useEffect(() => {
    cbRef.current = { onStart, onStage, onStdout, onStderr, onCompileError, onExit, onError, onClose };
  }, [onStart, onStage, onStdout, onStderr, onCompileError, onExit, onError, onClose]);

  const cleanup = useCallback(() => {
    if (wsRef.current) {
      try { wsRef.current.close(); } catch (_) { /* */ }
      wsRef.current = null;
    }
  }, []);

  const run = useCallback(
    ({ language, code, stdin, timeout = 10, files, entry_file }) => {
      // Close any prior stream
      cleanup();
      stageRef.current = null;
      stdoutBufRef.current = "";
      stderrBufRef.current = "";
      compileStderrRef.current = "";
      startedAtRef.current = Date.now();
      setLastResult(null);
      setStatus("running");
      setIsRunning(true);

      const url = buildWsURL("/api/run/stream");
      let ws;
      try {
        ws = new WebSocket(url);
      } catch (e) {
        setIsRunning(false);
        setStatus("error");
        cbRef.current.onError?.({ data: `ws_open_failed: ${e?.message || e}` });
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => {
        try {
          ws.send(JSON.stringify({ language, code, stdin, timeout, files: files || null, entry_file: entry_file || null }));
        } catch (_) { /* */ }
      };

      ws.onmessage = (ev) => {
        let msg;
        try { msg = JSON.parse(ev.data); } catch (_) { return; }

        switch (msg.type) {
          case "start":
            setActiveRunId(msg.run_id || null);
            cbRef.current.onStart?.(msg);
            break;
          case "stage":
            stageRef.current = msg.stage;
            cbRef.current.onStage?.(msg.stage, msg);
            break;
          case "stdout":
            stdoutBufRef.current += msg.data || "";
            cbRef.current.onStdout?.(msg.data || "", stageRef.current);
            break;
          case "stderr":
            stderrBufRef.current += msg.data || "";
            if (stageRef.current === "compile") {
              compileStderrRef.current += msg.data || "";
            }
            cbRef.current.onStderr?.(msg.data || "", stageRef.current);
            break;
          case "compile_error":
            cbRef.current.onCompileError?.({
              ...msg,
              text: compileStderrRef.current,
            });
            break;
          case "exit": {
            const result = {
              run_id: msg.run_id,
              stdout: stdoutBufRef.current,
              stderr: stderrBufRef.current,
              compile_error: msg.compile_error
                ? compileStderrRef.current || "compile failed"
                : null,
              exit_code: msg.exit_code,
              exec_time_ms: msg.exec_time_ms,
              run_time_ms: msg.run_time_ms,
              peak_memory_kb: msg.peak_memory_kb || 0,
              timed_out: !!msg.timed_out,
              stopped: !!msg.stopped,
            };
            setLastResult(result);
            setIsRunning(false);
            // Status precedence: a user-initiated `stopped` ALWAYS wins over
            // `timed_out`. The backend may flag both when Stop is clicked very
            // close to the wall-clock deadline (post-mortem normalisation in
            // sandbox._normalize_timed_out treats SIGKILL-near-deadline as a
            // timeout). For the UI we want: stopped > timed_out > compile_error
            // > success > error.
            if (result.stopped) setStatus("stopped");
            else if (result.timed_out) setStatus("timed_out");
            else if (result.compile_error) setStatus("error");
            else if (result.exit_code === 0) setStatus("success");
            else setStatus("error");
            cbRef.current.onExit?.(result);
            break;
          }
          case "error":
            cbRef.current.onError?.(msg);
            break;
          default:
            // ignore unknown frames
            break;
        }
      };

      ws.onerror = (e) => {
        cbRef.current.onError?.({ data: "ws_error" });
      };

      ws.onclose = (ev) => {
        wsRef.current = null;
        // If we never received an exit frame, finalize as error
        setIsRunning((wasRunning) => {
          if (wasRunning) {
            setStatus((s) => (s === "running" ? "error" : s));
          }
          return false;
        });
        cbRef.current.onClose?.(ev);
      };
    },
    [cleanup]
  );

  const stop = useCallback(() => {
    const ws = wsRef.current;
    if (!ws) {
      // eslint-disable-next-line no-console
      console.warn("[noefforts] stop() called but no active WS");
      return false;
    }
    if (ws.readyState !== WebSocket.OPEN) {
      // eslint-disable-next-line no-console
      console.warn("[noefforts] stop() called but WS not OPEN, state=", ws.readyState);
      return false;
    }
    try {
      ws.send(JSON.stringify({ type: "stop" }));
      // eslint-disable-next-line no-console
      console.log("[noefforts] stop() sent {type:'stop'} on WS");
      return true;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[noefforts] stop() send failed:", e);
      return false;
    }
  }, []);

  const sendStdin = useCallback((data) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    try {
      ws.send(JSON.stringify({ type: "stdin", data }));
      return true;
    } catch (_) {
      return false;
    }
  }, []);

  useEffect(() => () => cleanup(), [cleanup]);

  return { run, stop, sendStdin, isRunning, status, lastResult, activeRunId };
}
